import axios from "axios";
import { URLS } from "./urls";
import { BASE_URL } from "@/config/api";

const LOGIN_URL = "/sign-in";

// Utility for token management
const TokenService = {
  getToken: () => localStorage.getItem("token"),
  setToken: (token) => localStorage.setItem("token", token),
  removeToken: () => localStorage.removeItem("token"),
};

// Create an Axios instance with a base URL
const api = axios.create({
  baseURL: BASE_URL,
});

let refreshInProgress = null;

// Function to refresh the token
const refreshToken = async () => {
  if (!refreshInProgress) {
    refreshInProgress = axios
      .post(URLS.auth.TOKEN, null, {
        headers: {
          Authorization: `Bearer ${TokenService.getToken()}`,
        },
      })
      .then((response) => {
        const { token } = response.data;
        TokenService.setToken(token);
        refreshInProgress = null; // Reset cache
        return token;
      })
      .catch((error) => {
        TokenService.removeToken();
        refreshInProgress = null;
        window.location.href = LOGIN_URL; // Redirect to login
        throw error;
      });
  }

  return refreshInProgress;
};

// Request interceptor to attach the Authorization header
api.interceptors.request.use(
  (config) => {
    const token = TokenService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token expiration and retries
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = { ...error.config }; // Avoid mutating original request

    // Skip token refresh for login endpoint or if no token exists
    if (
      originalRequest.url !== URLS.auth.LOGIN_URL &&
      TokenService.getToken() &&
      (error.response?.status === 403 ||
        (error.response?.status === 401 && !originalRequest._retry))
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest); // Retry the original request
      } catch (err) {
        console.error("Retry after refresh failed:", err);
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
